import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  NgModule,
  LOCALE_ID,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import * as Sentry from '@sentry/angular';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import { Router } from '@angular/router';

import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';
import { JwtStorageService } from './shared';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import { LayoutModule as MobileLayoutModule } from './modules/mobile/_layout/layout.module';
import { LayoutModule as PcLayoutModule } from './modules/pc/_layout/layout.module';
import {
  I18NextModule,
  defaultInterpolationFormat,
  ITranslationService,
  I18NextLoadResult,
  I18NEXT_SERVICE,
  StrictErrorHandlingStrategy,
} from 'angular-i18next';
import HttpBackend from 'i18next-http-backend'; // i18next의 HTTP 백엔드 플러그인
import LanguageDetector from 'i18next-browser-languagedetector';

// import { HomeComponent as MobileHomeComponent } from './modules/mobile/home/home.component';
// import { HomeComponent as PcHomeComponent } from './modules/pc/home/home.component';
import { MainComponent as MobileMainComponent } from './modules/mobile/home/main.component';
import { MainComponent as PcMainComponent } from './modules/pc/home/main.component';
import { PageNotFoundComponent } from './shared/misc/page-not-found/page-not-found.component';

import { environment } from '../environments/environment';
import { StoreModule } from '@ngrx/store';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { cartReducer } from './store/cart';
import { indicatorReducer } from './store/indicator';
import { selectProductReducer } from './store/select-product';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { provideEnvironmentNgxMask } from 'ngx-mask';

registerLocaleData(localeEn, 'en');
registerLocaleData(localeEn, 'ko');
registerLocaleData(localeEn, 'vi');

export function jwtOptionsFactory(jwtStorageService: JwtStorageService) {
  return {
    tokenGetter: () => {
      return jwtStorageService.getToken();
    },
  };
}

const maskConfig: any = {
  validation: false,
};

const i18nextOptions = {
  whitelist: ['en', 'ko', 'vi'],
  languages: ['en', 'ko', 'vi'],
  fallbackLng: 'en',
  debug: true, // set debug?
  // load: 'all', // set debug?
  returnEmptyString: false,
  // returnNull: true,
  // returnedObjectHandler: (key: string, value: any, options: any) => null,
  returnObjects: true,
  ns: [
    'errors',
    'translation',
    // 'feature.rich_form'
  ],
  defaultNS: 'translation',
  interpolation: {
    format: I18NextModule.interpolationFormat(defaultInterpolationFormat),
  },
  // saveMissing: true,
  // backend plugin options
  backend: {
    loadPath: 'locales/{{lng}}/{{ns}}.json',
  },
  // lang detection plugin options
  detection: {
    // order and from where user language should be detected
    order: ['querystring', 'cookie', 'localStorage'],
    // , 'navigator', 'htmlTag', 'path', 'subdomain'],

    // keys or params to lookup language from
    lookupQuerystring: 'lang',
    lookupCookie: 'lang',
    lookupLocalStorage: 'lang',

    // cache user language on
    caches: ['localStorage', 'cookie'],
    excludeCacheFor: ['cimode'], // languages to not persist (cookie, localStorage)

    // optional expire and domain for set cookie
    cookieMinutes: 10080, // 7 days
    // cookieDomain: I18NEXT_LANG_COOKIE_DOMAIN
    checkWhitelist: true,
  },
};

// export function appInit(i18next: ITranslationService) {
//   return () => {
//     const promise: Promise<I18NextLoadResult> = i18next.use(XHR).use(LanguageDetector).init(i18nextOptions);
//     return promise;
//   };
// }

export function appInit(i18next: ITranslationService) {
  return () => {
    // `angular-i18next`를 사용할 경우, i18next를 직접 사용하는 것과 유사하게 설정할 수 있습니다.
    const promise: Promise<any> = i18next
      .use(HttpBackend) // i18next-http-backend 사용
      .use(LanguageDetector) // 언어 감지기 사용
      .init(i18nextOptions);

    return promise; // 초기화가 완료될 때까지 대기
  };
}

export function localeIdFactory(i18next: ITranslationService) {
  return i18next.language;
}

export const I18N_PROVIDERS = [
  provideAppInitializer(() => {
    const initializerFn = appInit(inject(I18NEXT_SERVICE));
    return initializerFn();
  }),
  {
    provide: LOCALE_ID,
    deps: [I18NEXT_SERVICE],
    useFactory: localeIdFactory,
  },
];

const SentryProviders = [
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
      showDialog: false,
    }),
  },
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  provideAppInitializer(() => {
    inject(Sentry.TraceService);
  }),
];

@NgModule({
  declarations: [AppComponent, PageNotFoundComponent, PcMainComponent, MobileMainComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    MobileLayoutModule,
    PcLayoutModule,
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    SharedModule,
    AppRoutingModule,
    // NgxLoadingModule.forRoot({}),
    StoreModule.forRoot({ cart: cartReducer, indicator: indicatorReducer, selectProduct: selectProductReducer }),
    // ToastrModule.forRoot({
    //     timeOut: 3000,
    //     positionClass: 'toast-top-right',
    //     preventDuplicates: true,
    // }),
    // DeviceDetectorModule.forRoot(),
    AngularSvgIconModule.forRoot(), // angular-svg-icon 모듈 추가
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [JwtStorageService],
      },
    }),
    I18NextModule.forRoot({
      errorHandlingStrategy: StrictErrorHandlingStrategy,
    }),
  ],

  providers: [
    provideZoneChangeDetection(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.never-use-dark-mode',
        },
      },
    }),
    provideEnvironmentNgxMask(maskConfig),
    I18N_PROVIDERS,
    SentryProviders,
    AppComponent,
    // { provide: LocationStrategy, useClass: HashLocationStrategy },
    AppRoutingModule,
    { provide: 'googleTagManagerId', useValue: environment.googleTagManagerId },
  ],
  exports: [],
  // entryComponents: [PcMainComponent, MobileMainComponent],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {}
}
